import React, { useState } from "react";

import FoundryBadge from "../components/foundry/FoundryBadge";
import FoundryButton from "../components/foundry/FoundryButton";
import FoundryCard from "../components/foundry/FoundryCard";
import SanctionsPageShell from "../components/foundry/SanctionsPageShell";

type ComboKeyForm = {
  customer: string;
  pol: string;
  pod: string;
  consignee: string;
  hsCode: string;
};

type ApiFetch = (path: string, init?: RequestInit) => Promise<Response>;

type ComboKeyCheck = {
  combo_key: string | null;
  shipment_type: string;
  hs_code: string | null;
  hs_lookup_code: string | null;
  pod_level: number | null;
  pod_land: string | null;
  pod_land_match: boolean | number;
  hs_risk: string | null;
  combo_status: string;
  normal_result: string;
  final_result: string;
  action_required: string;
};

type ComboKeyCheckerPageProps = {
  apiFetch: ApiFetch;
  canReviewComboKeys?: boolean;

};

type BadgeVariant = "default" | "orange" | "green" | "red" | "blue" | "purple";

const emptyForm: ComboKeyForm = {
  customer: "",
  pol: "",
  pod: "",
  consignee: "",
  hsCode: "",
};

const fields: { key: keyof ComboKeyForm; label: string; placeholder: string }[] = [
  { key: "customer", label: "Klant", placeholder: "Bijvoorbeeld: Jordex" },
  { key: "pol", label: "POL", placeholder: "Bijvoorbeeld: Rotterdam" },
  { key: "pod", label: "POD", placeholder: "Bijvoorbeeld: Rotterdam" },
  { key: "consignee", label: "Consignee / ontvanger", placeholder: "Naam ontvanger" },
  { key: "hsCode", label: "HS-code(s)", placeholder: "Bijvoorbeeld: 12345678, 87654321" },
];

function getStatusVariant(status?: string | null): BadgeVariant {
  switch (String(status || "").toUpperCase()) {
    case "ROOD": return "red";
    case "ORANJE": return "orange";
    case "GROEN": return "green";
    case "GOEDGEKEURD": return "purple";
    case "AFGEKEURD": return "red";
    case "GEEN MATCH": return "blue";
    default: return "default";
  }
}

function getPodLandDisplay(check: ComboKeyCheck) {
  return check.pod_land?.trim() || "GEEN MATCH";
}

function getComboDecision(comboStatus?: string | null) {
  const status = String(comboStatus || "").toUpperCase();
  if (status.includes("GOEDGEKEURD")) return "GOEDGEKEURD";
  if (status.includes("AFGEKEURD")) return "AFGEKEURD";
  return "NIEUW";
}

function getMissingFields(form: ComboKeyForm) {
  const labels: { key: keyof ComboKeyForm; label: string }[] = [
    { key: "customer", label: "Klant" },
    { key: "pol", label: "POL" },
    { key: "pod", label: "POD" },
    { key: "consignee", label: "Consignee / ontvanger" },
    { key: "hsCode", label: "HS-code(s)" },
  ];

  return labels.filter(({ key }) => !form[key].trim()).map(({ label }) => label);
}

function formatFieldList(fields: string[]) {
  if (fields.length <= 1) return fields[0] || "";
  if (fields.length === 2) return `${fields[0]} en ${fields[1]}`;
  return `${fields.slice(0, -1).join(", ")} en ${fields[fields.length - 1]}`;
}

function getHsCodeValidationMessage(hsCode: string) {
  const codes = hsCode
    .split(/[,;|\r\n]+/)
    .map((code) => code.trim())
    .filter(Boolean);

  if (codes.length === 0) {
    return "Vul minimaal één HS-code in.";
  }

  const invalidCode = codes.find((code) => code.toLowerCase() !== "bob" && !/^\d+$/.test(code));
  if (invalidCode) {
    return `HS-code '${invalidCode}' is ongeldig. Een HS-code mag alleen uit cijfers bestaan.`;
  }

  const shortCode = codes.find((code) => code.toLowerCase() !== "bob" && code.length < 6);
  if (shortCode) {
    return `HS-code '${shortCode}' is te kort. Vul minimaal 6 cijfers in.`;
  }

  return "";
}

function getNoMatchReasons(check: ComboKeyCheck, pod: string) {
  const reasons: string[] = [];
  if (check.pod_level === null || !check.pod_land?.trim() || !Boolean(check.pod_land_match)) {
    reasons.push(`Onbekende POD: ${pod || "onbekend"}.`);
  }
  if (!['GROEN', 'ORANJE', 'ROOD'].includes(String(check.hs_risk || "").toUpperCase())) {
    reasons.push(`Onbekende HS-code: ${check.hs_lookup_code || check.hs_code || "onbekend"}.`);
  }
  return reasons;
}

const ComboKeyCheckerPage: React.FC<ComboKeyCheckerPageProps> = ({ apiFetch, canReviewComboKeys = false }) => {
  const [form, setForm] = useState<ComboKeyForm>(emptyForm);
  const [showBobEasterEgg, setShowBobEasterEgg] = useState(false);
  const [checkResults, setCheckResults] = useState<ComboKeyCheck[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [shipmentType, setShipmentType] = useState<"IMPORT" | "EXPORT" | null>(null);
  const firstCheck = checkResults[0] || null;
  // The preview represents one Combo Key. With multiple HS codes the backend
  // creates a separate Combo Key per code, so only show the first entry here.
  const firstHsCode = form.hsCode.split(",")[0]?.trim() || "";
  const comboKeyPreview = [
    shipmentType === "IMPORT" ? "IMP" : shipmentType === "EXPORT" ? "EXP" : "...",
    form.customer,
    form.pol,
    form.pod,
    form.consignee,
    firstHsCode,
  ]
    .map((value) => value.trim() || "...")
    .join("-");
  const updateForm = (key: keyof ComboKeyForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setShowBobEasterEgg(false);
    setCheckResults([]);
    setCheckError("");
  };
  const isBobCombo = [
    form.customer,
    form.pol,
    form.pod,
    form.consignee,
    form.hsCode,
  ].every((value) => value.trim() !== "" && value.trim().toLowerCase() === "bob");
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowBobEasterEgg(false);
    setCheckError("");
    setCheckResults([]);

    const missingFields = [
      ...getMissingFields(form),
      ...(!shipmentType ? ["Type shipment"] : []),
    ];
    if (missingFields.length > 0) {
      setCheckError(`Niet ingevuld: ${formatFieldList(missingFields)}.`);
      return;
    }

    const hsCodeError = getHsCodeValidationMessage(form.hsCode);
    if (hsCodeError) {
      setCheckError(hsCodeError);
      return;
    }
    if (!shipmentType) {
      setCheckError("Type shipment ontbreekt: kies Import of Export.");
      return;
    }

    setShowBobEasterEgg(isBobCombo);
    setIsChecking(true);

    try {
      const response = await apiFetch("/api/combo-keys/check", {
        method: "POST",
        body: JSON.stringify({ ...form, shipmentType }),
      });
      const payload = await response.json().catch(() => null);

      const returnedChecks = Array.isArray(payload?.checks)
        ? payload.checks
        : payload?.check
          ? [payload.check]
          : [];

      if (!response.ok) {
        const details = Array.isArray(payload?.details) ? payload.details.filter(Boolean) : [];
        if (response.status === 401) {
          throw new Error("Je sessie is verlopen. Meld je opnieuw aan en probeer opnieuw.");
        }
        if (response.status === 403) {
          throw new Error("Je bent niet gemachtigd om de snelle controle uit te voeren.");
        }
        if (response.status === 404) {
          throw new Error("De Combo Key-controle is nog niet beschikbaar op de aangesloten backend.");
        }
        if (details.length > 0) {
          throw new Error(details.join(" "));
        }
        throw new Error(payload?.error || "De controle kon niet worden uitgevoerd. Probeer het opnieuw.");
      }

      if (!returnedChecks.length) {
        throw new Error("De Combo Key-controle gaf geen resultaat terug.");
      }

      const checks = returnedChecks as ComboKeyCheck[];
      setCheckResults(checks);
    } catch (error) {
      setCheckError(error instanceof Error ? error.message : "De controle kon niet worden uitgevoerd. Probeer het opnieuw.");
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <SanctionsPageShell active="check" canManage={canReviewComboKeys}>
          <div className="mx-auto max-w-3xl">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-orange-500">Shipment control</p>
                <h2 className="mt-2 text-2xl font-black text-slate-950 dark:text-white">Snelle sanctiecontrole</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500 dark:text-slate-400">Controleer een shipmentcombinatie op POD-niveau, landmatch en HS-risico.</p>
              </div>
            </div>

            <FoundryCard className="mt-7 p-5 sm:p-6">
              <form className="grid gap-5 sm:grid-cols-2" onSubmit={handleSubmit}>
                {fields.map((field) => (
                  <label key={field.key} className="block">
                    <span className="text-sm font-black text-slate-700 dark:text-slate-200">{field.label}</span>
                    <input disabled={isChecking} value={form[field.key]} onChange={(event) => updateForm(field.key, event.target.value)} placeholder={field.placeholder} className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:placeholder:text-slate-500 dark:focus:ring-orange-500/20" />
                  </label>
                ))}
                <div aria-label="Type shipment" className="pt-7">
                  <div className="flex w-full overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
                    <button type="button" disabled={isChecking} onClick={() => setShipmentType("IMPORT")} className={`flex-1 px-4 py-2.5 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60 ${shipmentType === "IMPORT" ? "bg-orange-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-white/5"}`}>Import</button>
                    <button type="button" disabled={isChecking} onClick={() => setShipmentType("EXPORT")} className={`flex-1 border-l border-slate-200 px-4 py-2.5 text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 ${shipmentType === "EXPORT" ? "bg-orange-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-white/5"}`}>Export</button>
                  </div>
                </div>
                <div className="sm:col-span-2 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-5 dark:border-white/10">
                  <FoundryButton type="submit" variant="primary" disabled={isChecking}>{isChecking ? "Controleren..." : "Controleren"}</FoundryButton>
                  <FoundryButton type="button" disabled={isChecking} onClick={() => { setForm(emptyForm); setShipmentType(null); setShowBobEasterEgg(false); setCheckResults([]); setCheckError(""); }}>Wissen</FoundryButton>
                </div>
              </form>
            </FoundryCard>

            <FoundryCard className="mt-5">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Resultaat</p>

              {showBobEasterEgg && (
                <div aria-live="polite" className="mt-4 overflow-hidden rounded-2xl border border-yellow-300 bg-gradient-to-r from-yellow-100 via-amber-50 to-yellow-100 p-5 text-center dark:border-yellow-400/30 dark:from-yellow-500/15 dark:via-orange-500/10 dark:to-yellow-500/15">
                  <p className="text-xs font-black uppercase tracking-[0.32em] text-amber-700 dark:text-yellow-200">Banana protocol</p>
                  <div aria-hidden="true" className="mt-3 flex justify-center gap-3 text-4xl sm:text-5xl">
                    <span className="animate-bounce [animation-delay:75ms]">👑</span>
                    <span className="animate-bounce">🍌</span>
                    <span className="animate-bounce [animation-delay:150ms]">🍌</span>
                    <span className="animate-bounce [animation-delay:300ms]">🍌</span>
                    <span className="animate-bounce [animation-delay:450ms]">🍌</span>
                    <span className="animate-bounce [animation-delay:600ms]">🍌</span>
                    <span className="animate-bounce [animation-delay:675ms]">👑</span>
                  </div>
                  <p className="mt-3 text-lg font-black text-amber-900 dark:text-yellow-100">Bob-modus geactiveerd!</p>
                  <p className="mt-1 text-sm font-bold text-amber-800/80 dark:text-yellow-100/80">Een banaanachtige verrassing voor deze bijzondere Combo Key.</p>
                  <img
                    src="/kingbob.gif"
                    alt="King Bob-viering"
                    className="mx-auto mt-4 max-h-56 w-full max-w-sm rounded-xl border border-amber-300 object-cover shadow-lg dark:border-yellow-400/30"
                  />
                </div>
              )}

              {checkError && (
                <div role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm leading-6 text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
                  {checkError}
                </div>
              )}

              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Combo Key</p>
                  {(showBobEasterEgg || isChecking || firstCheck) && (
                    <FoundryBadge variant={showBobEasterEgg ? "orange" : getStatusVariant(getComboDecision(firstCheck?.combo_status))}>
                      {showBobEasterEgg ? "Bob-modus" : isChecking ? "Controleren..." : getComboDecision(firstCheck?.combo_status)}
                    </FoundryBadge>
                  )}
                </div>
                <p className="mt-2 break-all font-mono text-sm font-bold text-slate-900 dark:text-white">{checkResults.length > 1 ? `${checkResults.length} Combo Keys gecontroleerd` : firstCheck?.combo_key || comboKeyPreview}</p>
              </div>

              {checkResults.length > 0 && (
                <div className="mt-4 space-y-3">
                  {checkResults.map((check) => {
                    const podLandDisplay = getPodLandDisplay(check);
                    const noMatchReasons = getNoMatchReasons(check, form.pod.trim());

                    return (
                      <div key={check.combo_key || check.hs_code || "empty"} className="rounded-xl border border-slate-200 p-4 dark:border-white/10">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="font-mono text-sm font-bold text-slate-900 dark:text-white">{check.combo_key || "Geen volledige Combo Key"}</p>
                          <FoundryBadge variant={getStatusVariant(check.normal_result)}>{check.normal_result}</FoundryBadge>
                        </div>
                        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">POD niveau</dt>
                            <dd className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">{check.pod_level ?? "Nog niet bepaald"}</dd>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">POD land</dt>
                            <dd className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">{podLandDisplay}</dd>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">HS risico</dt>
                            <dd className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">{check.hs_risk || "GEEN MATCH"}</dd>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">Type shipment</dt>
                            <dd className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">{check.shipment_type === "IMPORT" ? "Import" : "Export"}</dd>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">Combo status</dt>
                            <dd className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">{check.combo_status}</dd>
                          </div>
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">Resultaat</dt>
                            <dd className="mt-1 text-sm font-bold text-slate-700 dark:text-slate-200">{check.normal_result}</dd>
                          </div>
                          <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
                            <dt className="text-xs font-black uppercase tracking-[0.12em] text-slate-500">Actie vereist</dt>
                            <dd className="mt-1 text-base font-black leading-6 text-slate-900 dark:text-white">
                              {noMatchReasons.length > 0 ? (
                                <ul className="space-y-1">
                                  {noMatchReasons.map((reason) => <li key={reason}>{reason}</li>)}
                                </ul>
                              ) : check.action_required}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    );
                  })}
                </div>
              )}

            </FoundryCard>
          </div>
    </SanctionsPageShell>
  );
};

export default ComboKeyCheckerPage;
